/**
 * Waits for an element matching the selector to appear in the DOM.
 * Uses MutationObserver with polling fallback.
 *
 * Note: This checks for element **existence** only, not visibility.
 * Elements inside a React Native Web ScrollView may have zero dimensions
 * due to `flex: 1 1 0%` collapsing (`flex-basis: 0%`). The separate
 * `resolveVisibleElement` helper in `buildDriverSteps` handles finding
 * the correct visible descendant for driver.js positioning.
 *
 * @param selector - CSS selector for the element to wait for
 * @param timeout - Maximum time to wait in milliseconds (default: 5000)
 * @returns Promise that resolves with the element or rejects on timeout
 */
export const waitForElement = (selector: string, timeout = 5000): Promise<Element> => {
  return new Promise((resolve, reject) => {
    // Check if element already exists
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    let observer: MutationObserver | null = null;
    let pollingInterval: ReturnType<typeof setInterval> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // Set up MutationObserver
    observer = new MutationObserver(() => {
      const element = document.querySelector(selector);
      if (element) {
        cleanup();
        resolve(element);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Polling fallback (every 200ms)
    pollingInterval = setInterval(() => {
      const element = document.querySelector(selector);
      if (element) {
        cleanup();
        resolve(element);
      }
    }, 200);

    // Timeout
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`waitForElement: Timeout waiting for "${selector}" after ${timeout}ms`));
    }, timeout);
  });
};
